import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import TextField from '../../../../components/forms/TextField';
import FieldError from '../../../../components/forms/FieldError';
import Switch from '../../../../components/forms/Switch';
import {
    color_colors_decline,
    color_defaults_white,
    color_gradients_green_shift,
    color_shades_darkest,
    color_shades_darker,
    color_shades_light,
    color_shades_lighter,
    color_text_link,
} from '../../../../constants/colors';
import {
    formatTtsPronunciationsMap,
    getTtsPronunciationEntries,
    getTtsPronunciationsMapValidationError,
    normalizeTtsPronunciationsMap,
    serializeTtsPronunciationsMap,
    TtsPronunciationEntry,
} from './ttsPronunciationsMapUtils';

interface TtsPronunciationsMapEditorProps {
    name: string;
    value?: string;
    onChange: (value: string) => void;
    onBlur: () => void;
    onTouch: () => void;
    fieldContainerStyle?: string;
}

const emptyEntry = (): TtsPronunciationEntry => ({ source: '', replacement: '' });

const TtsPronunciationsMapEditor: React.FC<TtsPronunciationsMapEditorProps> = ({
    name,
    value,
    onChange,
    onBlur,
    onTouch,
    fieldContainerStyle,
}) => {
    const [advancedMode, setAdvancedMode] = useState(false);
    const [advancedValue, setAdvancedValue] = useState('');
    const [entries, setEntries] = useState([] as TtsPronunciationEntry[]);
    const skipNextValueSync = useRef(false);

    const syncFromValue = useCallback((nextValue?: string) => {
        const validationError = getTtsPronunciationsMapValidationError(nextValue);

        if (!!validationError.length) {
            setAdvancedValue(nextValue ?? '');
            setAdvancedMode(true);
            return;
        }

        setEntries(getTtsPronunciationEntries(nextValue));
        setAdvancedValue(formatTtsPronunciationsMap(nextValue));
    }, []);

    useEffect(() => {
        if (skipNextValueSync.current) {
            skipNextValueSync.current = false;
            return;
        }

        syncFromValue(value);
    }, [syncFromValue, value]);

    const applyBuilderChange = (nextEntries: TtsPronunciationEntry[]) => {
        setEntries(nextEntries);
        const nextValue = serializeTtsPronunciationsMap(nextEntries);

        setAdvancedValue(formatTtsPronunciationsMap(nextValue));
        skipNextValueSync.current = nextValue !== (value ?? '');
        onChange(nextValue);
        onTouch();
    };

    const handleEntryChange = (index: number, field: keyof TtsPronunciationEntry, fieldValue: string) => {
        const nextEntries = [...entries];
        const currentEntry = nextEntries[index] ?? emptyEntry();

        nextEntries[index] = {
            ...currentEntry,
            [field]: fieldValue,
        };

        applyBuilderChange(nextEntries);
    };

    const handleAddEntry = () => {
        setEntries((prevEntries) => [...prevEntries, emptyEntry()]);
        onTouch();
    };

    const handleRemoveEntry = (index: number) => {
        applyBuilderChange(entries.filter((_, currentIndex) => currentIndex !== index));
    };

    const handleAdvancedModeChange = (checked: boolean) => {
        if (checked) {
            setAdvancedValue(formatTtsPronunciationsMap(value));
            setAdvancedMode(true);
            return;
        }

        const validationError = getTtsPronunciationsMapValidationError(advancedValue);

        if (!!validationError.length) {
            onTouch();
            return;
        }

        const normalizedValue = normalizeTtsPronunciationsMap(advancedValue);

        syncFromValue(normalizedValue);
        skipNextValueSync.current = normalizedValue !== (value ?? '');
        onChange(normalizedValue);
        onTouch();
        setAdvancedMode(false);
    };

    const currentValue = advancedMode ? advancedValue : value;
    const validationError = getTtsPronunciationsMapValidationError(currentValue);
    const payloadPreview = !!validationError.length ? '' : normalizeTtsPronunciationsMap(currentValue);

    return (
        <Container>
            <HeaderRow>
                <HeaderCopy>
                    <Label>TTS Pronunciations</Label>
                    <Description>
                        Define text replacements before TTS runs. Use this for brand names, acronyms, abbreviations, and menu items.
                    </Description>
                    <HelperText>
                        This is text substitution before TTS, not a phoneme or vendor lexicon feature.
                    </HelperText>
                </HeaderCopy>
                <AdvancedToggle
                    checked={advancedMode}
                    onChange={handleAdvancedModeChange}
                    label="Advanced JSON"
                    detail={advancedMode ? 'Raw editor enabled' : 'Use the structured builder'}
                />
            </HeaderRow>

            {!advancedMode && (
                <EditorCard>
                    <CardHeader>
                        <CardTitle>Pronunciation Rules</CardTitle>
                        <InlineButton type="button" onClick={handleAddEntry}>
                            + Add Rule
                        </InlineButton>
                    </CardHeader>
                    <CardDescription>
                        Source text is matched case-insensitively. Pronounced as should be the plain-language text you want TTS to say. Use a single space if you want to strip a symbol from the spoken output.
                    </CardDescription>
                    {!entries.length && (
                        <EmptyState>
                            No pronunciation rules yet. Add a row to define replacements like <code>Voicify</code> to <code>voice-ify</code> or <code>BBQ</code> to <code>barbecue</code>.
                        </EmptyState>
                    )}
                    {entries.map((entry, index) => (
                        <EntryRow key={`tts-pronunciation-entry-${index}`}>
                            <EntryField
                                useForm={false}
                                label={index === 0 ? 'Source Text' : undefined}
                                placeholder="Voicify"
                                value={entry.source}
                                onChange={(e) => handleEntryChange(index, 'source', e.target.value)}
                                onBlur={onBlur}
                                fieldContainerStyle={fieldContainerStyle}
                            />
                            <EntryField
                                useForm={false}
                                label={index === 0 ? 'Pronounced As' : undefined}
                                placeholder="voice-ify"
                                value={entry.replacement}
                                onChange={(e) => handleEntryChange(index, 'replacement', e.target.value)}
                                onBlur={onBlur}
                                fieldContainerStyle={fieldContainerStyle}
                            />
                            <RemoveTextButton type="button" onClick={() => handleRemoveEntry(index)}>
                                Remove
                            </RemoveTextButton>
                        </EntryRow>
                    ))}
                </EditorCard>
            )}

            {advancedMode && (
                <EditorCard>
                    <CardHeader>
                        <CardTitle>Advanced JSON</CardTitle>
                    </CardHeader>
                    <CardDescription>
                        Enter a flat JSON object where each key is the matched text and each value is the replacement text sent to TTS.
                    </CardDescription>
                    <RawJsonTextArea
                        name={name}
                        value={advancedValue}
                        placeholder={`{
  "Voicify": "voice-ify",
  "Dr.": "Doctor",
  "IVR": "I V R",
  "R": " "
}`}
                        onChange={(e) => {
                            const nextValue = e.target.value;
                            setAdvancedValue(nextValue);
                            skipNextValueSync.current = nextValue !== (value ?? '');
                            onChange(nextValue);
                            onTouch();
                        }}
                        onBlur={onBlur}
                    />
                </EditorCard>
            )}

            <PreviewCard>
                <CardHeader>
                    <CardTitle>Payload Preview</CardTitle>
                </CardHeader>
                <CardDescription>
                    This is the JSON string that will be sent as <code>ttsPronunciationsMap</code>.
                </CardDescription>
                <PreviewBox>
                    {!!payloadPreview.length ? payloadPreview : 'Empty string will be sent.'}
                </PreviewBox>
            </PreviewCard>

            <FieldError name={name} />
        </Container>
    );
};

const Container = styled.div`
    margin-bottom: 24px;
`;

const HeaderRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 24px;
    margin-bottom: 16px;
    flex-wrap: wrap;
`;

const HeaderCopy = styled.div`
    flex: 1;
    min-width: 280px;
`;

const Label = styled.p`
    font-size: 14px;
    color: ${color_shades_darkest};
    margin: 0 0 8px 0;
`;

const Description = styled.p`
    font-size: 14px;
    color: ${color_shades_darker};
    line-height: 1.5;
    margin: 0;
`;

const HelperText = styled.p`
    font-size: 12px;
    color: ${color_shades_darker};
    margin: 8px 0 0 0;
`;

const EditorCard = styled.div`
    border: 1px solid ${color_shades_light};
    border-radius: 8px;
    background: ${color_defaults_white};
    padding: 16px;
    margin-bottom: 16px;
`;

const PreviewCard = styled(EditorCard)`
    margin-bottom: 0;
`;

const CardHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    margin-bottom: 8px;
`;

const CardTitle = styled.h4`
    font-size: 14px;
    font-weight: 600;
    color: ${color_shades_darkest};
    margin: 0;
`;

const CardDescription = styled.p`
    font-size: 13px;
    color: ${color_shades_darker};
    line-height: 1.5;
    margin: 0 0 16px 0;
`;

const EmptyState = styled.div`
    font-size: 13px;
    color: ${color_shades_darker};
    border: 1px dashed ${color_shades_light};
    border-radius: 8px;
    padding: 16px;
`;

const EntryRow = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) auto;
    gap: 12px;
    align-items: end;
    margin-bottom: 8px;
`;

const EntryField = styled(TextField)`
    margin-bottom: 0;
`;

const InlineButton = styled.button`
    border: none;
    background: none;
    color: ${color_text_link};
    font-size: 13px;
    cursor: pointer;
    padding: 0;

    &:hover {
        color: ${color_shades_darker};
    }
`;

const RemoveTextButton = styled(InlineButton)`
    margin-bottom: 24px;

    &:hover {
        color: ${color_colors_decline};
    }
`;

const RawJsonTextArea = styled.textarea`
    width: 100%;
    min-height: 220px;
    border-radius: 8px;
    border: 1px solid ${color_shades_light};
    padding: 16px;
    font-family: Consolas, 'Courier New', monospace;
    font-size: 13px;
    line-height: 1.5;
    color: ${color_shades_darkest};
    resize: vertical;
    box-sizing: border-box;

    &:focus {
        outline: none;
        border-color: ${color_shades_darker};
    }
`;

const PreviewBox = styled.pre`
    margin: 0;
    border-radius: 8px;
    border: 1px solid ${color_shades_light};
    background: ${color_defaults_white};
    padding: 16px;
    white-space: pre-wrap;
    word-break: break-word;
    font-family: Consolas, 'Courier New', monospace;
    font-size: 13px;
    line-height: 1.5;
    color: ${color_shades_darkest};
`;

const AdvancedToggle = styled(Switch)`
    flex-shrink: 0;
`;

export default TtsPronunciationsMapEditor;
